import React, { useContext, useEffect, useMemo, useState } from 'react';
import './DashboardAdmin.css';
import WelcomeCard from './partials/WelcomeCard';
import { Clock, CheckCircle2, FileText, XCircle, ChevronDown, Filter } from 'lucide-react';
import StatisticsChart from './partials/StatisticsChart';
import { AuthContext } from '../../context/AuthContext';
import { DashboardService } from '../../services/DashboardService';
import Preloader from '../../components/Preloader';
import CircularProgress from './partials/CircularProgress';
import TopVendedoresChart from './partials/TopVendedoresChart';
import { TypesService } from '../../services/TypesService';
import TopProductosChart from './partials/TopProductosChart';
import SalesEvolutionChart from './partials/SalesEvolutionChart';
import ProvincesRanking from './partials/ProvincesRanking';
import { UsuarioService } from '../../services/UsuarioService';
import RecaudacionMensualChart from './partials/RecaudacionMensualChart';

const getNombreCompleto = (usuario) => {
  if (!usuario) return '';
  return [usuario.nombre, usuario.apellidoPaterno, usuario.apellidoMaterno]
    .filter(Boolean).join(' ').trim();
};

const DashboardAdmin = () => {
  const { usuario, token } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [regiones, setRegiones] = useState(null);
  const [topVendedores, setTopVendedores] = useState(null);
  const [porProductos, setPorProductos] = useState(null);
  const [evolucionMensual, setEvolucionMensual] = useState(null);
  const [porProvincias, setPorProvincias] = useState(null);
  const [recaudacionMensual, setRecaudacionMensual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [estados, setEstados] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState('');
  const [supervisoresDisponibles, setSupervisoresDisponibles] = useState([]);
  const [filtroSupervisor, setFiltroSupervisor] = useState("");

  const tienePermisoRecaudacion = useMemo(() =>
    usuario?.permisos?.includes("list_venta"),
    [usuario]);

  useEffect(() => {
    const loadStaticData = async () => {
      if (!token) return;
      try {
        const [statusData, supervisors] = await Promise.all([
          TypesService.get({ token, table: 'estado_cliente' }),
          UsuarioService.fetchSupervisores(token)
        ]);
        setEstados(statusData);
        setSupervisoresDisponibles(supervisors);
      } catch (err) {
        console.error("Error cargando datos estáticos", err);
      }
    };
    loadStaticData();
  }, [token]);

  useEffect(() => {
    if (!token) return;

    const loadMetrics = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await DashboardService.getInfo(token, fechaInicio, fechaFin, estadoSeleccionado, filtroSupervisor);
        setStats(data.stats);
        setRegiones(data.porRegiones);
        setTopVendedores(data.topVendedores);
        setPorProductos(data.porProductos);
        setEvolucionMensual(data.evolucionMensual);
        setPorProvincias(data.porProvincias);
        setRecaudacionMensual(data.recaudacionMensual);
      } catch (error) {
        console.error("Error cargando métricas", error);
        setError("Error al cargar las estadísticas.");
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadMetrics();
    }, 300);

    return () => clearTimeout(timer);
  }, [token, fechaInicio, fechaFin, estadoSeleccionado, filtroSupervisor]);

  const calcularPorcentaje = (valor) => {
    if (!stats?.total) return 0;
    return Math.round((valor / stats.total) * 100);
  };


  if (loading) return <Preloader />;
  if (error) return <div className="error-container">{error}</div>;

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-grid">
        <div className="left-column">
          <WelcomeCard usuario={usuario} total={stats?.total} />

          <div className="metrics-row">
            <div className="light-intensity-card">
              <div className="light-intensity-header">
                <h3 className="light-intensity-title">Ventas Pendientes</h3>
                <Clock className="light-intensity-icon" size={20} />
              </div>
              <div className="light-intensity-progress">
                <CircularProgress percentage={calcularPorcentaje(stats?.pendientes)} color="#EAB308" showLabels={false} />
              </div>
              {stats?.pendientes > 0 && (
                <div className="light-intensity-value">
                  <span className="value-label">Cantidad</span>
                  <span className="value-number">{stats.pendientes}</span>
                </div>
              )}
            </div>

            <div className="light-intensity-card">
              <div className="light-intensity-header">
                <h3 className="light-intensity-title">Ventas Instaladas</h3>
                <CheckCircle2 className="light-intensity-icon" size={20} />
              </div>
              <div className="light-intensity-progress">
                <CircularProgress percentage={calcularPorcentaje(stats?.instaladas)} color="#22C55E" showLabels={false} />
              </div>
              {stats?.instaladas > 0 && (
                <div className="light-intensity-value">
                  <span className="value-label">Cantidad</span>
                  <span className="value-number">{stats.instaladas}</span>
                </div>
              )}
            </div>
            <div className="light-intensity-card">
              <div className="light-intensity-header">
                <h3 className="light-intensity-title">Ventas Reportadas</h3>
                <FileText className="light-intensity-icon" size={20} />
              </div>
              <div className="light-intensity-progress">
                <CircularProgress percentage={calcularPorcentaje(stats?.reportadas)} color="#3B82F6" showLabels={false} />
              </div>
              {stats?.reportadas > 0 && (
                <div className="light-intensity-value">
                  <span className="value-label">Cantidad</span>
                  <span className="value-number">{stats.reportadas}</span>
                </div>
              )}
            </div>
            <div className="light-intensity-card">
              <div className="light-intensity-header">
                <h3 className="light-intensity-title">Ventas Anuladas</h3>
                <XCircle className="light-intensity-icon" size={20} />
              </div>
              <div className="light-intensity-progress">
                <CircularProgress percentage={calcularPorcentaje(stats?.anuladas)} color="#EF4444" showLabels={false} />
              </div>
              {stats?.anuladas > 0 && (
                <div className="light-intensity-value">
                  <span className="value-label">Cantidad</span>
                  <span className="value-number">{stats.anuladas}</span>
                </div>
              )}
            </div>

          </div>

          <div className="center-column">
            <SalesEvolutionChart data={evolucionMensual || []} />
            <div className={`charts-row ${tienePermisoRecaudacion ? '' : 'full-width-province'}`}>

              <ProvincesRanking data={porProvincias || []} />

              {tienePermisoRecaudacion && (
                <RecaudacionMensualChart data={recaudacionMensual || []} />
              )}

            </div>

          </div>

        </div>
        <div className="right-column">
          <button
            className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            <ChevronDown className={`arrow ${showFilters ? 'up' : ''}`} size={18} />
          </button>

          <div className={`filter-collapsible ${showFilters ? 'open' : ''}`}>
            <div className="filter-bar-vertical">
              <div className="filter-item">
                <label htmlFor='fecha-desde'>Desde</label>
                <input
                  type="date"
                  className="modern-input"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                />
              </div>

              <div className="filter-item">
                <label htmlFor="fecha-hasta">Hasta</label>
                <input
                  type="date"
                  className="modern-input"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                />
              </div>

              <div className="filter-item">
                <label htmlFor="fecha-estado">Estado</label>
                <select className="modern-select" value={estadoSeleccionado}
                  onChange={(e) => setEstadoSeleccionado(e.target.value)}>
                  <option value="">Todos los estados</option>
                  {estados.map((estado) => (
                    <option key={estado.id} value={estado.id}>
                      {estado.descripcion}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filtro-item">
                <label htmlFor="filtroJefe">Filtrar por Supervisor:</label>
                <select
                  id="filtroJefe"
                  value={filtroSupervisor}
                  onChange={(e) => setFiltroSupervisor(e.target.value)}
                  className="filtro-select"
                >
                  <option value="">Todos los supervisores</option>
                  {supervisoresDisponibles.map((supervisor) => (
                    <option key={supervisor.id} value={supervisor.id}>
                      {getNombreCompleto(supervisor)}
                    </option>
                  ))}
                </select>
              </div>

              <button className="btn-reset" onClick={() => { setFechaInicio(''); setFechaFin(''); setEstadoSeleccionado('') }}>
                Limpiar
              </button>
            </div>
          </div>

          <TopVendedoresChart data={topVendedores || []} />
          <TopProductosChart data={porProductos || []} />
          <StatisticsChart regionesData={regiones} />
        </div>
      </div>
    </div >
  );
};

export default DashboardAdmin;